
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getCompanyInvitations } from '@/services/invitationService';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatEmailForDisplay } from '@/utils/emailUtils';

interface InvitationsListProps {
  companyId: string;
}

// Define the type for company invitations
interface CompanyInvitation {
  id: string;
  company_id: string;
  email: string | { type: string; email: string };
  role: string;
  created_at: string;
  status: string;
  notes: string;
  updated_at: string;
  updated_by: string;
  user_id: string | null;
  invite_token: string;
  expires_at: string | null;
}

export const InvitationsList: React.FC<InvitationsListProps> = ({ companyId }) => {
  const { data: invitations, isLoading } = useQuery({
    queryKey: ['company-invitations', companyId],
    queryFn: () => getCompanyInvitations(companyId)
  });

  if (isLoading) {
    return <div>Loading invitations...</div>;
  }

  if (!invitations?.length) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pending Invitations</CardTitle>
        <CardDescription>List of people invited to join the company</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {invitations.map((invitation: CompanyInvitation) => (
            <div
              key={invitation.id}
              className="flex items-center justify-between p-4 border rounded-lg"
            >
              <div>
                <div className="font-medium">{formatEmailForDisplay(invitation.email)}</div>
                <div className="text-sm text-muted-foreground">
                  Invited {new Date(invitation.created_at).toLocaleDateString()}
                </div>
              </div>
              <Badge>{invitation.role}</Badge>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
